import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { normalizeImportItems } from "./importListLayout";
import {
  canStartDownload,
  downloadFilename,
  fetchContacts,
  orderColumns,
  resolveDownloadScope,
  triggerCsvDownload,
} from "./exportContactsCsv";
import "./WorldDbDownloadModal.css";

const WorldDbDownloadModal = ({
  userId,
  importOptions = [],
  initialListName = "",
  onClose,
}) => {
  const lists = useMemo(() => normalizeImportItems(importOptions), [importOptions]);
  const presetList = lists.some((item) => item.name === initialListName) ? initialListName : "";

  const [mode, setMode] = useState(presetList ? "lists" : "all");
  const [selected, setSelected] = useState(() => (presetList ? new Set([presetList]) : new Set()));
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [processed, setProcessed] = useState(0);
  const [error, setError] = useState("");
  const aliveRef = useRef(true);

  useEffect(() => () => { aliveRef.current = false; }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [busy, onClose]);

  const selectedNames = useMemo(() => Array.from(selected), [selected]);
  const canDownload = canStartDownload(mode, selectedNames) && !!userId && !busy;

  const shownLists = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return lists;
    return lists.filter((item) => String(item.name || "").toLowerCase().includes(term));
  }, [lists, search]);

  const toggleList = (name) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
    setMode("lists");
  };

  const selectAllVisible = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      shownLists.forEach((item) => next.add(item.name));
      return next;
    });
    setMode("lists");
  };

  const handleDownload = async () => {
    if (!canDownload) return;
    setError("");
    setBusy(true);
    setProcessed(0);
    try {
      const { importNames } = resolveDownloadScope(mode, selectedNames);
      const { rows, keys } = await fetchContacts({
        userId,
        importNames,
        onProgress: (n) => {
          if (aliveRef.current) setProcessed(n);
        },
      });
      if (!aliveRef.current) return;
      if (rows.length === 0) {
        setError(mode === "lists"
          ? "No contacts on the selected lists."
          : "Your contact database is empty — nothing to export.");
        return;
      }
      triggerCsvDownload(rows, orderColumns(keys), downloadFilename(mode, selectedNames));
      onClose();
    } catch (err) {
      console.error("[WorldDbDownload] export failed:", err);
      if (aliveRef.current) setError(err?.message || "Export failed. Please try again.");
    } finally {
      if (aliveRef.current) setBusy(false);
    }
  };

  const selectedCountLabel = mode === "lists"
    ? `${selectedNames.length} list${selectedNames.length === 1 ? "" : "s"} selected`
    : "All contacts";

  return createPortal(
    <div className="wdbDl-overlay" onClick={() => { if (!busy) onClose(); }}>
      <div
        className="wdbDl"
        role="dialog"
        aria-modal="true"
        aria-labelledby="wdbDl-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wdbDl-head">
          <div>
            <h3 id="wdbDl-title">Download List</h3>
            <p>Export contacts as CSV with every field and the complete data.</p>
          </div>
          <button
            type="button"
            className="wdbDl-close"
            onClick={onClose}
            disabled={busy}
            aria-label="Close"
          >
            ×
          </button>
        </header>

        <div className="wdbDl-body">
          <button
            type="button"
            className={`wdbDl-choice${mode === "all" ? " is-on" : ""}`}
            onClick={() => setMode("all")}
            disabled={busy}
          >
            <span className={`wdbDl-radio${mode === "all" ? " is-on" : ""}`} />
            <span className="wdbDl-choiceText">
              <strong>All lists</strong>
              <em>Every contact in the database, including entries without a list.</em>
            </span>
          </button>

          <button
            type="button"
            className={`wdbDl-choice${mode === "lists" ? " is-on" : ""}`}
            onClick={() => setMode("lists")}
            disabled={busy || lists.length === 0}
          >
            <span className={`wdbDl-radio${mode === "lists" ? " is-on" : ""}`} />
            <span className="wdbDl-choiceText">
              <strong>Specific lists</strong>
              <em>
                {lists.length === 0
                  ? "No import lists yet."
                  : "Pick one or more import lists."}
              </em>
            </span>
          </button>

          {mode === "lists" && lists.length > 0 && (
            <div className="wdbDl-lists">
              <div className="wdbDl-listsBar">
                <label className="wdbDl-search">
                  <span className="wdbDl-sr">Search lists</span>
                  <input
                    type="search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Search lists"
                    disabled={busy}
                  />
                </label>
                <button
                  type="button"
                  className="wdbDl-link"
                  onClick={selectAllVisible}
                  disabled={busy || shownLists.length === 0}
                >
                  Select all
                </button>
              </div>

              <div className="wdbDl-list" role="listbox" aria-multiselectable="true">
                {shownLists.map((item) => {
                  const on = selected.has(item.name);
                  return (
                    <button
                      key={item.name}
                      type="button"
                      className={`wdbDl-item${on ? " is-on" : ""}`}
                      onClick={() => toggleList(item.name)}
                      disabled={busy}
                    >
                      <span className={`wdbDl-check${on ? " is-on" : ""}`} aria-hidden="true">
                        {on && (
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        )}
                      </span>
                      <span className="wdbDl-itemBody">
                        <span className="wdbDl-itemName">{item.name}</span>
                        {item.imported_count != null && (
                          <span className="wdbDl-itemMeta">{item.imported_count} contacts</span>
                        )}
                      </span>
                    </button>
                  );
                })}
                {shownLists.length === 0 && (
                  <div className="wdbDl-empty">No lists match “{search.trim()}”</div>
                )}
              </div>
            </div>
          )}

          {busy && (
            <div className="wdbDl-progress">
              <div className="wdbDl-progressBar">
                <div className="wdbDl-progressFill" />
              </div>
              <span>
                {processed > 0
                  ? `${processed.toLocaleString("en-US")} contacts loaded…`
                  : "Preparing export…"}
              </span>
            </div>
          )}

          {error && <p className="wdbDl-error">{error}</p>}
        </div>

        <footer className="wdbDl-foot">
          <span className="wdbDl-counter">{selectedCountLabel}</span>
          <div className="wdbDl-actions">
            <button type="button" className="altBtn altBtn--petrol" onClick={onClose} disabled={busy}>
              Cancel
            </button>
            <button
              type="button"
              className="altBtn altBtn--save"
              onClick={handleDownload}
              disabled={!canDownload}
            >
              {busy ? (
                <>
                  <span className="wdbDl-spinner" />
                  Downloading…
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" y1="15" x2="12" y2="3" />
                  </svg>
                  Download CSV
                </>
              )}
            </button>
          </div>
        </footer>
      </div>
    </div>,
    document.body
  );
};

export default WorldDbDownloadModal;
